/* Umbrella Union — Release Player (TES + ASL)
 * Requires: assets/uu-media-coordinator.js
 * Scope: section root via [data-uu-release-player-root]
 */
(function () {
  'use strict';

  const ROOT = (window.UmbrellaUnion = window.UmbrellaUnion || {});

  const stateByRoot = new WeakMap();

  function qs(root, sel) {
    return root ? root.querySelector(sel) : null;
  }

  function qsa(root, sel) {
    return root ? Array.from(root.querySelectorAll(sel)) : [];
  }

  function safeText(el, value) {
    if (!el) return;
    el.textContent = value || '';
  }

  function safeAttr(el, name, value) {
    if (!el) return;
    if (value) el.setAttribute(name, value);
    else el.removeAttribute(name);
  }

  function initRoot(root) {
    if (!root || stateByRoot.has(root)) return;

    const sectionId = root.getAttribute('data-section-id');
    const playerId = root.getAttribute('data-player-id') || sectionId || 'main';

    const audio = qs(root, 'audio[data-uu-audio]');
    const rows = qsa(root, '[data-uu-track]');
    const btnPrev = qs(root, '[data-uu-prev]');
    const btnNext = qs(root, '[data-uu-next]');
    const btnShuffle = qs(root, '[data-uu-shuffle]');

    const activeArt = qs(root, '[data-uu-active-art]');
    const activeTitle = qs(root, '[data-uu-active-title]');
    const activeArtist = qs(root, '[data-uu-active-artist]');

    const st = {
      sectionId,
      playerId,
      audio,
      rows,
      btnPrev,
      btnNext,
      btnShuffle,
      activeArt,
      activeTitle,
      activeArtist,
      currentIndex: 0,
      shuffle: false,
      onRowClick: [],
      onNext: null,
      onPrev: null,
      onShuffle: null,
      onEnded: null,
      onPlay: null,
    };

    stateByRoot.set(root, st);

    if (ROOT.mediaCoordinator && sectionId) {
      ROOT.mediaCoordinator.registerSection(sectionId, { root });
    }

    function markActive(index, isPlaying) {
      st.rows.forEach((r) => r.classList.remove('is-active', 'is-playing'));
      const row = st.rows[index];
      if (!row) return;
      row.classList.add('is-active');
      if (isPlaying) row.classList.add('is-playing');
    }

    function loadTrack(index, autoPlay) {
      if (!st.audio || !st.rows.length) return;

      let nextIndex = index;
      if (nextIndex < 0) nextIndex = st.rows.length - 1;
      if (nextIndex >= st.rows.length) nextIndex = 0;
      st.currentIndex = nextIndex;

      const row = st.rows[nextIndex];
      const src = row.getAttribute('data-src');
      const img = row.getAttribute('data-img');
      const title = row.getAttribute('data-title');
      const artist = row.getAttribute('data-artist');

      if (src && st.audio.currentSrc !== src) {
        st.audio.src = src;
        st.audio.load();
      }

      if (img) safeAttr(st.activeArt, 'src', img);
      safeText(st.activeTitle, title);
      safeText(st.activeArtist, artist);

      markActive(nextIndex, false);

      if (autoPlay) {
        const playPromise = st.audio.play();
        if (playPromise && typeof playPromise.catch === 'function') playPromise.catch(() => {});
        markActive(nextIndex, true);
      }
    }

    function next(autoPlay) {
      loadTrack(st.currentIndex + 1, !!autoPlay);
    }

    function prev(autoPlay) {
      loadTrack(st.currentIndex - 1, !!autoPlay);
    }

    function toggleShuffle() {
      st.shuffle = !st.shuffle;
      if (st.btnShuffle) st.btnShuffle.classList.toggle('is-active', st.shuffle);
    }

    // Bind controls
    if (st.btnNext) {
      st.onNext = () => next(true);
      st.btnNext.addEventListener('click', st.onNext);
    }

    if (st.btnPrev) {
      st.onPrev = () => prev(true);
      st.btnPrev.addEventListener('click', st.onPrev);
    }

    if (st.btnShuffle) {
      st.onShuffle = () => toggleShuffle();
      st.btnShuffle.addEventListener('click', st.onShuffle);
    }

    st.rows.forEach((row, idx) => {
      const fn = () => loadTrack(idx, true);
      st.onRowClick.push(fn);
      row.addEventListener('click', fn);
    });

    if (st.audio) {
      st.onEnded = () => {
        if (!st.rows.length) return;
        if (st.shuffle) {
          const r = Math.floor(Math.random() * st.rows.length);
          loadTrack(r, true);
        } else {
          next(true);
        }
      };
      st.audio.addEventListener('ended', st.onEnded);

      // ASL: pause all others via coordinator
      st.onPlay = () => {
        if (ROOT.mediaCoordinator) ROOT.mediaCoordinator.pauseAllExcept({ type: 'audio', el: st.audio });
      };
      st.audio.addEventListener('play', st.onPlay);
    }

    // Initial
    if (st.rows.length) loadTrack(0, false);
  }

  function destroyRoot(root) {
    const st = stateByRoot.get(root);
    if (!st) return;

    if (st.btnNext && st.onNext) st.btnNext.removeEventListener('click', st.onNext);
    if (st.btnPrev && st.onPrev) st.btnPrev.removeEventListener('click', st.onPrev);
    if (st.btnShuffle && st.onShuffle) st.btnShuffle.removeEventListener('click', st.onShuffle);

    st.rows.forEach((row, idx) => {
      const fn = st.onRowClick[idx];
      if (fn) row.removeEventListener('click', fn);
    });

    if (st.audio) {
      if (st.onEnded) st.audio.removeEventListener('ended', st.onEnded);
      if (st.onPlay) st.audio.removeEventListener('play', st.onPlay);
    }

    if (ROOT.mediaCoordinator && st.sectionId) {
      ROOT.mediaCoordinator.unregisterSection(st.sectionId);
    }

    stateByRoot.delete(root);
  }

  function boot() {
    qsa(document, '[data-uu-release-player-root]').forEach(initRoot);
  }

  // Expose minimal API
  ROOT.releasePlayer = ROOT.releasePlayer || {};
  ROOT.releasePlayer.init = (root) => initRoot(root);
  ROOT.releasePlayer.destroy = (root) => destroyRoot(root);

  // DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  // Theme Editor lifecycle
  document.addEventListener('shopify:section:load', (evt) => {
    const root = evt && evt.target ? evt.target.querySelector('[data-uu-release-player-root]') : null;
    if (root) initRoot(root);
  });

  document.addEventListener('shopify:section:unload', (evt) => {
    const root = evt && evt.target ? evt.target.querySelector('[data-uu-release-player-root]') : null;
    if (root) destroyRoot(root);
  });
})();
